import { useBillingAPI } from "@certn/api";
import { Layout, Loader, Text } from "@certn/cascadia-react";
import {
  getIncompletePublicInvoiceMessage,
  isRenderablePublicInvoiceDocument,
  StatusOnlyPublicInvoiceDocument,
} from "@certn/utils";
import { css } from "@emotion/css";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { InvoiceDetailPageHeader } from "./components/InvoiceDetailPageHeader";
import { Invoice } from "../../../../../apps/pdf-lambda/app/src/components/invoice-main/index";
import { InvoiceProps } from "../../../../pdf-lambda/src/schemas-and-types";
import { AppFooter } from "../../components/app-footer/AppFooter";
import {
  StripePaymentContextState,
  useStripePaymentContext,
} from "../../components/stripe/StripePaymentContext";

// We're not maintaining a definition of invoice.document_data in backend so we need to manually use the type from the
// invoice PDF generator here.
export interface PublicInvoiceDetailData {
  public_document_data:
    | InvoiceProps["invoiceData"]
    | StatusOnlyPublicInvoiceDocument;
}

export function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { state: stripeState, clearState: clearStripe } =
    useStripePaymentContext();
  const { invoiceId } = useParams();
  const {
    data: invoiceData,
    isLoading,
    mutate,
  } = useBillingAPI<
    "/api/internal/billing/invoices/{id}/public/",
    PublicInvoiceDetailData
  >(
    `/api/internal/billing/invoices/${invoiceId}/public/` as "/api/internal/billing/invoices/{id}/public/",
    { method: "GET" },
    {
      // Refresh the page every 3 seconds if we're waiting for the webhook to finish
      // ==> Polling while waiting for the webhook to finish
      refreshInterval: () => (stripeState === "pending-webhook" ? 3e3 : 0),
    }
  );

  useEffect(() => {
    // While waiting for payment, anytime the state or invoice data changes,
    // check if we've found what we're waiting for, ie. the invoice got paid
    if (
      invoiceData?.public_document_data?.status === "PAID" &&
      stripeState === "pending-webhook"
    ) {
      // And clear the state to stop polling
      clearStripe();
    }
  }, [invoiceData, stripeState, clearStripe]);

  if (!invoiceData || isLoading) {
    return <Loader />;
  }

  if (!isRenderablePublicInvoiceDocument(invoiceData.public_document_data)) {
    const message = getIncompletePublicInvoiceMessage(
      invoiceData.public_document_data.status,
      t
    );
    return (
      <Layout
        menuPosition="left"
        layout="applicant/narrow"
        footer={<AppFooter />}
      >
        <Text style="global/body-primary">{message}</Text>
      </Layout>
    );
  }

  const processingPaymentOverlay = (
    <div className={styles.processingPayment}>
      <Loader />
      <Text style="global/heading-large">
        {t("Processing your payment...")}
      </Text>
    </div>
  );

  return (
    <Layout
      menuPosition="left"
      layout="applicant/narrow"
      header={
        <InvoiceDetailPageHeader
          invoiceData={invoiceData?.public_document_data}
          onPONumberUpdated={() => mutate()}
          onInvoiceUpdated={() => mutate()}
        />
      }
      footer={<AppFooter />}
    >
      <Invoice
        componentName="invoice"
        invoiceData={invoiceData.public_document_data}
      />
      {/* Block the UI while requests/webhooks are pending */}
      {(
        [
          "pending-webhook",
          "fetching-client-secret",
        ] satisfies StripePaymentContextState[]
      ).some((x) => x === stripeState) && processingPaymentOverlay}
    </Layout>
  );
}

const styles = {
  processingPayment: css({
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    zIndex: 10000,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  }),
};
